/* Theme Demo — standalone "High-End Enterprise SaaS" theme preview.
   Route: /theme-demo. Not linked anywhere — preview only. */
import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  ArrowUpRight,
  PhoneCall,
  Users,
  DollarSign,
  TrendingUp,
  Send,
} from "lucide-react";

const KPIs = [
  { icon: PhoneCall, label: "Qualified Calls", value: "12,408", delta: "+14.2%" },
  { icon: Users, label: "Active Publishers", value: "1,284", delta: "+6.8%" },
  { icon: DollarSign, label: "Payout Volume", value: "$284,500", delta: "+11.4%" },
  { icon: TrendingUp, label: "Conversion Rate", value: "9.6%", delta: "+0.8%" },
];

const BARS = [42, 58, 47, 66, 54, 72, 61, 80, 69, 88, 76, 95];

export default function ThemeDemoPage() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-['Inter',-apple-system,sans-serif] antialiased">
      {/* Header */}
      <header className="border-b border-slate-200" data-testid="theme-demo-header">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-700 flex items-center justify-center">
              <PhoneCall className="w-4 h-4 text-white" strokeWidth={2.25} />
            </div>
            <div className="leading-tight">
              <p className="font-semibold text-[15px] tracking-tight">Digital Call Network</p>
              <p className="text-[11px] text-slate-500">Enterprise SaaS Theme Preview</p>
            </div>
          </div>
          <Button
            data-testid="theme-demo-header-cta"
            className="rounded-lg bg-indigo-700 hover:bg-indigo-800 text-white shadow-sm transition-colors duration-150"
          >
            Get Started
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14 sm:py-20 space-y-16">
        {/* Intro */}
        <section className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700">
            Theme Demo
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            High-End Enterprise SaaS
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            A clean, minimalist system built on pure white surfaces, Inter typography,
            slate borders, and deep indigo actions — designed for clarity at scale.
          </p>
        </section>

        {/* Dashboard Card */}
        <section data-testid="theme-demo-dashboard-card">
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader className="border-b border-slate-200 pb-5">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold tracking-tight">
                    Network Performance
                  </CardTitle>
                  <CardDescription className="text-sm text-slate-500 mt-1">
                    Last 12 weeks · all traffic sources
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="theme-demo-export-btn"
                  className="rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Export
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
              {/* KPI row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {KPIs.map((kpi) => (
                  <div key={kpi.label} data-testid={`theme-demo-kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 text-slate-500">
                      <kpi.icon className="w-4 h-4" strokeWidth={2} />
                      <span className="text-xs font-medium uppercase tracking-wider">{kpi.label}</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-semibold tracking-tight text-slate-900">{kpi.value}</span>
                      <span className="text-xs font-semibold text-emerald-600">{kpi.delta}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Minimal bar chart */}
              <div>
                <div className="flex items-end gap-2 h-28" data-testid="theme-demo-chart">
                  {BARS.map((h, i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end h-full">
                      <div
                        className={`rounded-sm transition-colors ${i === BARS.length - 1 ? "bg-indigo-700" : "bg-slate-200 hover:bg-indigo-200"}`}
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-slate-400 font-medium">
                  <span>Week 1</span>
                  <span>Week 12</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Contact Form */}
        <section className="max-w-xl" data-testid="theme-demo-contact-section">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Contact Sales</h2>
          <p className="mt-2 text-sm text-slate-600">
            See how the theme handles form elements — labels, inputs, focus states, and the primary action.
          </p>

          {sent ? (
            <div
              className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800"
              data-testid="theme-demo-form-success"
            >
              <span className="font-semibold">Message received.</span> This is a demo — no data was sent.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="td-name" className="text-sm font-medium text-slate-700">Full name</Label>
                  <Input
                    id="td-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Cooper"
                    data-testid="theme-demo-input-name"
                    className="rounded-lg border-slate-300 focus-visible:ring-indigo-600 focus-visible:border-indigo-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="td-email" className="text-sm font-medium text-slate-700">Work email</Label>
                  <Input
                    id="td-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@company.com"
                    data-testid="theme-demo-input-email"
                    className="rounded-lg border-slate-300 focus-visible:ring-indigo-600 focus-visible:border-indigo-600"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="td-company" className="text-sm font-medium text-slate-700">Company</Label>
                <Input
                  id="td-company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Acme Media Group"
                  data-testid="theme-demo-input-company"
                  className="rounded-lg border-slate-300 focus-visible:ring-indigo-600 focus-visible:border-indigo-600"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="td-message" className="text-sm font-medium text-slate-700">Message</Label>
                <Textarea
                  id="td-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your call volume and verticals…"
                  data-testid="theme-demo-input-message"
                  className="rounded-lg border-slate-300 focus-visible:ring-indigo-600 focus-visible:border-indigo-600 resize-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="theme-demo-submit-btn"
                className="rounded-lg bg-indigo-700 hover:bg-indigo-800 text-white shadow-sm transition-colors duration-150"
              >
                <Send className="w-4 h-4" />
                Send Message
              </Button>
            </form>
          )}
        </section>
      </main>

      <footer className="border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-slate-400">
          Theme demo — preview build for Digital Master Media LLC. Not linked in production navigation.
        </div>
      </footer>
    </div>
  );
}
